// Live co-writing in Write's editor (W9b), registered as one of its extensions (`registerEditorExtension`):
//
// - this writer's caret and typing go to the project's presence (store/presence.ts);
// - the other members' carets and selections are drawn (Web/editor's `remoteCarets`), placed afresh whenever someone
//   moves or text arrives, and carried through edits in between;
// - after another device's change to a shown fragment, the paragraphs it touched get the editor's fix-up when the merge
//   left markers that don't pair (`mergeFix`: deletions only, one undoable step, synced like any edit). Which characters
//   were markup comes from the document's history: the text before the change (this device's) and at the heads the
//   change brought (the sender's).
//
// It listens to the live project directly. Its remote listener runs before the editor's own (Scrivenings subscribes
// after the editor is made), so it notes the text before the change and does its work in a microtask, once the
// editor has applied the change.
import * as A from '@automerge/automerge';
import type { Extension } from '@codemirror/state';
import { ViewPlugin, type EditorView, type ViewUpdate } from '@codemirror/view';
import { ROOT, SwiftReader } from '@writing-suite/core';
import {
  changedRange, fragmentBody, localSelection, markupInParents, mergeFix, rebalanceSpec, remoteCaretPositions, remoteCarets, setRemoteCarets,
  shownFragmentIds, touchesMarkup,
  type CursorBackend,
} from '@writing-suite/editor';
import { registerEditorExtension } from '../views/Scrivenings.tsx';
import { presenceOf } from './presence.ts';
import type { LiveProject } from './project.ts';

const FRAGMENT = 'fragment.';

class Cowriting {
  private offs: (() => void)[] = [];
  private destroyed = false;
  private pushQueued = false;
  private lastCarets = '';

  constructor(readonly view: EditorView, readonly live: LiveProject) {
    const presence = presenceOf(live);
    if (presence) this.offs.push(presence.subscribe(() => this.queuePush()));
    this.offs.push(live.onRemote((doc, beforeHeads, after) => this.remote(doc, beforeHeads, after)));
    this.queuePush();
    queueMicrotask(() => this.reportCaret());
    // Tests read the others' carets as the editor has them (each fragment's UTF-16 offsets, and where each is drawn).
    Object.defineProperty(view.dom, 'wsRemoteCarets', { configurable: true, get: () => remoteCaretPositions(view.state) });
  }

  update(u: ViewUpdate) {
    // A project nobody else can open: nothing to report (this runs with every keystroke).
    const presence = presenceOf(this.live);
    if (!presence?.active) return;
    if (u.selectionSet || u.docChanged) this.reportCaret();
    const typed = u.transactions.some((tr) => tr.docChanged && (tr.isUserEvent('input') || tr.isUserEvent('delete')) && !tr.isUserEvent('input.rebalance'));
    if (typed) presence.typed();
  }

  destroy() {
    this.destroyed = true;
    for (const off of this.offs) off();
    presenceOf(this.live)?.caret(null);
  }

  private reportCaret() {
    if (this.destroyed) return;
    presenceOf(this.live)?.caret(localSelection(this.view.state));
  }

  /** The others' carets placed afresh (in a microtask: after the editor has the text they're resolved in). */
  private queuePush() {
    if (this.pushQueued) return;
    this.pushQueued = true;
    queueMicrotask(() => {
      this.pushQueued = false;
      if (this.destroyed) return;
      const presence = presenceOf(this.live);
      if (!presence) return;
      const shown = new Set(shownFragmentIds(this.view.state));
      // Only when the editor is in step with the documents: else the next change places them.
      for (const id of shown) if (presence.roster.entries().some((e) => e.payload.fragment === id) && fragmentBody(this.view.state, id) !== this.live.text(id)) return;
      const spec = presence.carets(shown);
      const key = JSON.stringify(spec);
      if (key === this.lastCarets && !spec.set.length) return;
      this.lastCarets = key;
      this.view.dispatch({ effects: setRemoteCarets.of(spec) });
    });
  }

  /** Another device's change: noted now, checked once the editor has applied it. */
  private remote(docName: string, beforeHeads: A.Heads | null, after: A.Doc<unknown>) {
    if (!docName.startsWith(FRAGMENT) || this.destroyed) return;
    const id = docName.slice(FRAGMENT.length);
    const before = fragmentBody(this.view.state, id);
    if (before === null) return;
    const afterHeads = [...A.getHeads(after)];
    queueMicrotask(() => {
      if (this.destroyed) return;
      this.queuePush();
      if (!beforeHeads || this.view.state.readOnly) return;
      const doc = this.live.doc(docName);
      // Moved on again meanwhile (the next change is checked in its turn), or the editor isn't in step.
      if (!doc || A.getHeads(doc).join() !== afterHeads.join()) return;
      const now = fragmentBody(this.view.state, id);
      if (now === null || now === before) return;
      const touched = changedRange(before, now);
      // Plain prose typed: no markers near it, nothing more to read.
      if (!touched || !touchesMarkup(now, touched[0], touched[1]) || now !== this.live.text(id)) return;
      const theirHeads = afterHeads.filter((h) => !beforeHeads.includes(h));
      const reader = (heads?: string[]) => new SwiftReader(doc, heads);
      const textObj = reader().get(ROOT, 'text');
      if (!textObj || textObj[0] !== 'text') return;
      const theirs = theirHeads.length ? reader(theirHeads).string(ROOT, 'text') ?? null : null;
      const wasMarkup = markupInParents(A.getBackend(doc) as unknown as CursorBackend, textObj[1] as string, [
        { heads: [...beforeHeads], text: before },
        ...(theirs !== null ? [{ heads: theirHeads, text: theirs }] : []),
      ]);
      const fix = mergeFix(now, touched, before, theirs, wasMarkup);
      const spec = rebalanceSpec(this.view.state, id, fix);
      if (spec) this.view.dispatch(spec);
    });
  }
}

/** Write's editor co-writes when the project is live. */
export function cowritingExtension(live: LiveProject): Extension {
  return [remoteCarets(), ViewPlugin.define((view) => new Cowriting(view, live))];
}

registerEditorExtension(({ live }) => (live ? cowritingExtension(live) : []));
